import React, { useState, useEffect } from 'react';
import { invoicesAPI, jobsAPI, quotesAPI, emailAPI } from '../services/api';
import { DatePicker } from '../components/DatePicker';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Search, Edit, Send, FileText, Trash2, CheckCircle, Download, Eye, Archive } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { PDFViewer } from '../components/ui/pdf-viewer';

const statusColors = {
  draft: 'status-draft',
  sent: 'status-sent',
  paid: 'status-paid',
  overdue: 'status-overdue',
  cancelled: 'status-cancelled',
};

export const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfTitle, setPdfTitle] = useState('');
  const [formData, setFormData] = useState({
    job_id: '',
    quote_id: '',
    client_name: '',
    client_email: '',
    items: [{ description: '', quantity: 1, unit_price: 0 }],
    due_date: '',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [invoicesRes, jobsRes, quotesRes] = await Promise.all([
        invoicesAPI.getAll(),
        jobsAPI.getAll(),
        quotesAPI.getAll()
      ]);
      setInvoices(invoicesRes.data);
      setJobs(jobsRes.data);
      setQuotes(quotesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleJobChange = (jobId) => {
    const job = jobs.find(j => j.id === jobId);
    setFormData({
      ...formData,
      job_id: jobId,
      client_name: job?.client_name || '',
      client_email: job?.client_email || '',
    });
  };

  const handleQuoteChange = (quoteId) => {
    const quote = quotes.find(q => q.id === quoteId);
    if (quote) {
      setFormData({
        ...formData,
        quote_id: quoteId,
        job_id: quote.job_id,
        client_name: quote.client_name,
        client_email: quote.client_email || '',
        items: quote.items.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price
        })),
      });
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0 }]
    });
  };

  const removeItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = field === 'quantity' || field === 'unit_price' ? parseFloat(value) || 0 : value;
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await invoicesAPI.create(formData);
      toast.success('Invoice created');
      fetchData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    try {
      await invoicesAPI.update(invoiceId, { status: newStatus });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteInvoice = async (invoice) => {
    if (!window.confirm(`Delete invoice ${invoice.invoice_number}? This cannot be undone. Tip: choose "Archive" instead if you want to keep the audit trail.`)) return;
    try {
      await invoicesAPI.delete(invoice.id);
      toast.success('Invoice deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  const handleArchiveInvoice = async (invoice) => {
    if (invoice.status === 'cancelled') {
      toast.info('Invoice is already archived');
      return;
    }
    if (!window.confirm(`Archive invoice ${invoice.invoice_number}? It will stop counting toward totals/burndown, but stays in the audit trail.`)) return;
    try {
      await invoicesAPI.archive(invoice.id);
      toast.success('Invoice archived');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive invoice');
    }
  };

  const handleToggleGst = async (invoice, nextChargeGst) => {
    try {
      const subtotal = parseFloat(invoice.subtotal || 0);
      const tax = nextChargeGst ? Math.round(subtotal * 0.1 * 100) / 100 : 0;
      const total = Math.round((subtotal + tax) * 100) / 100;
      await invoicesAPI.update(invoice.id, {
        charge_gst: nextChargeGst,
        tax,
        total,
        amount_due: total,
      });
      toast.success(`GST ${nextChargeGst ? 'enabled' : 'disabled'}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update invoice');
    }
  };

  const handleDownloadPdf = async (invoiceId, invoiceNumber) => {
    try {
      const response = await invoicesAPI.downloadPdf(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice-${invoiceNumber}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleViewPdf = async (invoiceId, invoiceNumber) => {
    try {
      const response = await invoicesAPI.downloadPdf(invoiceId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setPdfTitle(`Invoice ${invoiceNumber}`);
      setPdfViewerOpen(true);
    } catch (error) {
      toast.error('Failed to load PDF');
    }
  };

  const handleSendInvoice = async (invoice) => {
    if (!invoice.client_email) {
      toast.error('No client email address');
      return;
    }
    
    const htmlContent = `
      <h2>Invoice from ConstructOne</h2>
      <p>Dear ${invoice.client_name},</p>
      <p>Please find your invoice <strong>${invoice.invoice_number}</strong>.</p>
      <p><strong>Total Due: $${invoice.total?.toFixed(2)}</strong></p>
      ${invoice.due_date ? `<p>Due Date: ${format(new Date(invoice.due_date), 'MMMM d, yyyy')}</p>` : ''}
      <p>Thank you for your business!</p>
      <p>ConstructOne Team</p>
    `;
    
    try {
      await emailAPI.send({
        recipient_email: invoice.client_email,
        subject: `Invoice ${invoice.invoice_number} from ConstructOne`,
        html_content: htmlContent,
        related_type: 'invoice',
        related_id: invoice.id
      });
      await invoicesAPI.update(invoice.id, { status: 'sent' });
      toast.success(`Invoice sent to ${invoice.client_email}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to send invoice');
    }
  };

  const resetForm = () => {
    setDialogOpen(false);
    setFormData({
      job_id: '',
      quote_id: '',
      client_name: '',
      client_email: '',
      items: [{ description: '', quantity: 1, unit_price: 0 }],
      due_date: '',
      notes: '',
    });
  };

  const filteredInvoices = invoices.filter(inv =>
    inv.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inv.client_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="invoices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Invoices</h1>
          <p className="text-muted-foreground mt-1">Create and manage invoices</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button className="uppercase font-bold tracking-wide rounded-sm" data-testid="add-invoice-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Invoice
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Invoice</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>From Quote (Optional)</Label>
                  <Select value={formData.quote_id} onValueChange={handleQuoteChange}>
                    <SelectTrigger className="bg-input border-border rounded-sm">
                      <SelectValue placeholder="Select a quote" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {quotes.filter(q => q.status === 'accepted').map(quote => (
                        <SelectItem key={quote.id} value={quote.id}>{quote.quote_number} - {quote.client_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Select Job *</Label>
                  <Select value={formData.job_id} onValueChange={handleJobChange}>
                    <SelectTrigger className="bg-input border-border rounded-sm" data-testid="invoice-job">
                      <SelectValue placeholder="Select a job" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {jobs.map(job => (
                        <SelectItem key={job.id} value={job.id}>{job.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Client Name *</Label>
                  <Input
                    value={formData.client_name}
                    onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                    required
                    className="bg-input border-border rounded-sm"
                    data-testid="invoice-client-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <DatePicker
                    value={formData.due_date}
                    onChange={(d) => setFormData({ ...formData, due_date: d })}
                    placeholder="Due date"
                    data-testid="invoice-due-date"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Client Email</Label>
                <Input
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                  className="bg-input border-border rounded-sm"
                />
              </div>

              {/* Line Items */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Line Items</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addItem} className="rounded-sm">
                    <Plus className="h-4 w-4 mr-1" /> Add Item
                  </Button>
                </div>
                <div className="space-y-2 border border-border rounded-sm p-3">
                  {formData.items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-center">
                      <Input
                        placeholder="Description"
                        value={item.description}
                        onChange={(e) => updateItem(index, 'description', e.target.value)}
                        className="col-span-6 bg-input border-border rounded-sm text-sm"
                      />
                      <Input
                        type="number"
                        placeholder="Qty"
                        value={item.quantity}
                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                        className="col-span-2 bg-input border-border rounded-sm text-sm"
                      />
                      <Input
                        type="number"
                        step="0.01"
                        placeholder="Price"
                        value={item.unit_price}
                        onChange={(e) => updateItem(index, 'unit_price', e.target.value)}
                        className="col-span-3 bg-input border-border rounded-sm text-sm"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeItem(index)}
                        className="col-span-1"
                        disabled={formData.items.length === 1}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex justify-end pt-2 border-t border-border">
                    <p className="font-mono font-bold">
                      Subtotal: ${calculateTotal().toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-input border-border rounded-sm"
                  rows={3}
                  placeholder="Payment terms, bank details, etc..."
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetForm} className="rounded-sm">Cancel</Button>
                <Button type="submit" className="uppercase font-bold tracking-wide rounded-sm" data-testid="invoice-submit">
                  Create Invoice
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search invoices..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-input border-border rounded-sm"
          data-testid="invoice-search"
        />
      </div>

      {/* Table */}
      <Card className="bg-card border-border rounded-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <FileText className="h-12 w-12 mb-4" />
              <p>No invoices found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>GST</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="w-32">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.map((invoice) => (
                  <TableRow key={invoice.id} className="border-border" data-testid={`invoice-row-${invoice.id}`}>
                    <TableCell className="font-mono text-sm">
                      {invoice.invoice_number}
                      {invoice.invoice_kind === 'progress' && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded">
                          Progress{invoice.progress_pct ? ` ${Number(invoice.progress_pct)}%` : ''}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{invoice.client_name}</TableCell>
                    <TableCell className="font-mono font-bold">${invoice.total?.toFixed(2)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={invoice.charge_gst !== false}
                          onCheckedChange={(v) => handleToggleGst(invoice, v)}
                          data-testid={`gst-toggle-${invoice.id}`}
                        />
                        <span className={`text-xs ${invoice.charge_gst === false ? 'text-orange-400' : 'text-green-400'}`}>
                          {invoice.charge_gst === false ? 'OFF' : 'ON'}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {invoice.due_date ? format(new Date(invoice.due_date), 'MMM d, yyyy') : '-'}
                    </TableCell>
                    <TableCell>
                      <Select value={invoice.status} onValueChange={(v) => handleStatusChange(invoice.id, v)}>
                        <SelectTrigger className={`w-auto h-auto px-2 py-1 border ${statusColors[invoice.status]} rounded-sm text-xs font-mono uppercase`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="sent">Sent</SelectItem>
                          <SelectItem value="paid">Paid</SelectItem>
                          <SelectItem value="overdue">Overdue</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(invoice.created_at), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" title="View PDF" onClick={() => handleViewPdf(invoice.id, invoice.invoice_number)}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Download PDF" onClick={() => handleDownloadPdf(invoice.id, invoice.invoice_number)}>
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Send Invoice" onClick={() => handleSendInvoice(invoice)}>
                          <Send className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Mark as Paid" onClick={() => handleStatusChange(invoice.id, 'paid')}>
                          <CheckCircle className="h-4 w-4 text-green-400" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Archive" onClick={() => handleArchiveInvoice(invoice)} data-testid={`archive-invoice-${invoice.id}`} disabled={invoice.status === 'cancelled'}>
                          <Archive className="h-4 w-4 text-yellow-400" />
                        </Button>
                        <Button variant="ghost" size="icon" title="Delete Invoice" onClick={() => handleDeleteInvoice(invoice)} data-testid={`delete-invoice-${invoice.id}`}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* PDF Viewer Modal */}
      <PDFViewer
        isOpen={pdfViewerOpen}
        onClose={() => setPdfViewerOpen(false)}
        pdfUrl={pdfUrl}
        title={pdfTitle}
      />
    </div>
  );
};

export default InvoicesPage;
